import { isAnalyticsOptedOut } from './analytics-identity.js';
import { cleanAttributionPath, cleanCampaignTag, isAttributionTransitHost, parseSourceAttribution, parseSourceAttributionJson,
  SOURCE_ATTRIBUTION_STORAGE_KEY, type ExternalSource, type SourceAttribution } from '../shared/source-attribution.js';

type AttributionStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function browserStorage(): AttributionStorage | null {
  try { return window.localStorage; } catch { return null; }
}

export function readSourceAttribution(storage: AttributionStorage | null = browserStorage(), now = Date.now()): SourceAttribution | null {
  if (!storage) return null;
  try {
    const internalAccount = 'document' in globalThis
      && document.cookie.split(';').some((part) => part.trim() === 'internal_analytics=1');
    if (internalAccount || isAnalyticsOptedOut(storage)) {
      storage.removeItem(SOURCE_ATTRIBUTION_STORAGE_KEY);
      return null;
    }
    const saved = parseSourceAttributionJson(storage.getItem(SOURCE_ATTRIBUTION_STORAGE_KEY), now);
    if (!saved) storage.removeItem(SOURCE_ATTRIBUTION_STORAGE_KEY);
    return saved ?? { version: 1, first: null, last: null };
  } catch { return null; }
}

function externalEntrySource(href: string, referrer: string, now: number): ExternalSource | null {
  const url = new URL(href);
  let host: string | null = null;
  if (referrer) {
    const referringUrl = new URL(referrer);
    if (referringUrl.hostname === url.hostname || isAttributionTransitHost(referringUrl.hostname)) return null;
    if (!['https:', 'http:'].includes(referringUrl.protocol)) return null;
    host = referringUrl.hostname.toLowerCase();
  }
  const source: ExternalSource = {
    referrer_host: host,
    landing_page: cleanAttributionPath(url.pathname),
    utm_source: cleanCampaignTag(url.searchParams.get('utm_source')),
    utm_medium: cleanCampaignTag(url.searchParams.get('utm_medium')),
    utm_campaign: cleanCampaignTag(url.searchParams.get('utm_campaign')),
    captured_at: now,
  };
  if (!host && !source.utm_source && !source.utm_medium && !source.utm_campaign) return null;
  return source;
}

export function captureSourceAttribution({
  href = 'window' in globalThis ? window.location.href : '',
  referrer = 'document' in globalThis ? document.referrer : '',
  storage = browserStorage(),
  now = Date.now(),
}: { href?: string; referrer?: string; storage?: AttributionStorage | null; now?: number } = {}): SourceAttribution | null {
  if (!storage) return null;
  try {
    const existing = readSourceAttribution(storage, now);
    if (!existing) return null;
    const source = externalEntrySource(href, referrer, now);
    if (!source) return existing;
    const result = parseSourceAttribution({ version: 1, first: existing.first ?? source, last: source }, now);
    if (!result) return existing;
    storage.setItem(SOURCE_ATTRIBUTION_STORAGE_KEY, JSON.stringify(result));
    return result;
  } catch { return null; }
}
